import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { useShowFlash } from "@sprout/flash.tsx";
import useTranslation from "@hooks/use_translation.ts";
import { useSafePostHog } from "@hooks/use_posthog.ts";
import type { JobRecommendation } from "@components/dashboard/job_recommendation_card.tsx";
import type { RecommendationFeedback } from "@components/dashboard/recommendation_feedback_buttons.tsx";
import type { DislikeReason } from "@components/dashboard/dislike_reason_popover.tsx";
import { recommendationsQueryKey } from "./use_recommendations.ts";

const useRecommendationFeedback = (source: string) => {
  const queryClient = useQueryClient();
  const showFlash = useShowFlash();
  const posthog = useSafePostHog();
  const { t } = useTranslation("dashboard");

  const writeFeedback = (jobId: number, feedback: RecommendationFeedback | null) =>
    queryClient.setQueriesData<JobRecommendation[]>(
      { queryKey: recommendationsQueryKey() },
      (current) => current?.map((rec) => (rec.id === jobId ? { ...rec, feedback } : rec)),
    );

  return async (
    job: JobRecommendation,
    next: RecommendationFeedback | null,
    reason?: DislikeReason,
  ) => {
    const previous = job.feedback;

    writeFeedback(job.id, next);

    try {
      await axios.post(job.feedbackPath, {
        feedback: next ?? "",
        feedback_reasons: reason?.reasons ?? [],
        feedback_comment: reason?.comment ?? "",
      });

      posthog?.capture("recommendation_feedback_submitted", {
        job_post_id: job.id,
        feedback: next,
        previous_feedback: previous,
        source,
        feedback_reasons: reason?.reasons ?? [],
        feedback_comment: reason?.comment || null,
      });
    } catch {
      writeFeedback(job.id, previous);
      showFlash(t("errors.there_was_a_problem"), "error");
    }
  };
};

export default useRecommendationFeedback;
